import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {Workstream} from 'client';
import {normalizeStrategySummary} from 'components/DSYS/editor/nodes/strategyEntries';
import typeaheadStyles from 'components/DSYS/editor/nodes/TypeaheadNode.module.css';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import type {Column} from 'components/DSYS/Table';
import {Table} from 'components/DSYS/Table';
import {ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import {useStrategyEntries} from 'views/planning/hooks';
import {FadeIn, useHasMounted} from 'views/workstreams/page/FadeIn';

import {CreateSwitchboardZeroStateTrigger} from './SwitchboardZeroState';
import styles from './WorkstreamDetailsShell.module.css';
import {SectionToggleButton} from './WorkstreamSectionCard';
import tableStyles from './WorkstreamTable.module.css';

interface StrategyTableRow {
  id: string;
}

const STRATEGY_COLUMNS: Column<StrategyTableRow>[] = [
  {type: 'obstacles', minWidth: 320},
  {type: 'guiding_principles', minWidth: 320},
];

export const WorkstreamStrategySection = ({
  isExpanded,
  onExpandedChange,
  workstream,
  steadyHeader = false,
}: {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  workstream: Workstream;
  // Keep the header one height open or closed (see .activityHeaderSteady).
  steadyHeader?: boolean;
}) => {
  const editorDocId = workstream.editor_doc_id;
  const mounted = useHasMounted();
  const {data: strategyEntries} = useStrategyEntries(editorDocId ?? '');
  const {
    obstacles_and_opportunities: obstaclesAndOpportunities,
    guiding_principles: guidingPrinciples,
  } = normalizeStrategySummary(strategyEntries) ?? {
    obstacles_and_opportunities: [],
    guiding_principles: [],
  };
  const headerClassName = [
    styles.activityHeader,
    !isExpanded && styles.activityHeaderCollapsed,
    steadyHeader && styles.activityHeaderSteady,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={styles.activitySection} id="strategy-section">
      <Box className={headerClassName}>
        <SectionToggleButton
          fold="strategy"
          title={t('strategy')}
          isExpanded={isExpanded}
          onToggle={() => onExpandedChange(!isExpanded)}
          ariaLabel={isExpanded ? t('collapse_strategy') : t('expand_strategy')}
        />
        {isExpanded && editorDocId ? (
          <Button
            href={ROUTE_PATHS.PLANNING_DOC.replace(':id', editorDocId)}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant="outline"
          >
            {t('open_switchboard')}
          </Button>
        ) : null}
      </Box>

      {isExpanded ? (
        // Fades in when opened after the page arrived.
        <FadeIn enabled={mounted}>
          {editorDocId ? (
            <Table
              columns={STRATEGY_COLUMNS}
              data={[{id: 'strategy-summary'}]}
              className={styles.strategyTable}
              headerTextSize="small"
              columnLabelOverrides={{
                obstacles: 'strategy_kernel_obstacles_and_opportunities',
                guiding_principles: 'strategy_kernel_guiding_policies',
              }}
            >
              <Table.Header />
              <Table.Body>
                <Table.Row key="strategy-row">
                  <Table.Cell>
                    {obstaclesAndOpportunities.length > 0 ? (
                      <div className={typeaheadStyles.obstaclesAndOpportunitiesContainer}>
                        {obstaclesAndOpportunities.map((entry, index) => (
                          <div
                            key={`obstacle-${index}`}
                            className={typeaheadStyles.obstaclesAndOpportunitiesReadOnlyRow}
                          >
                            <span className={typeaheadStyles.obstaclesAndOpportunitiesLabel}>
                              {`O${index + 1}:`}
                            </span>
                            <span className={typeaheadStyles.obstaclesAndOpportunitiesReadOnlyText}>
                              {parseAndExtractTextFromLexicalJson(entry.strategy_value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Text size="small" color="subtle">
                        -
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {guidingPrinciples.length > 0 ? (
                      <div className={typeaheadStyles.obstaclesAndOpportunitiesContainer}>
                        {guidingPrinciples.map((entry, index) => (
                          <div
                            key={`principle-${index}`}
                            className={typeaheadStyles.obstaclesAndOpportunitiesReadOnlyRow}
                          >
                            <span className={typeaheadStyles.obstaclesAndOpportunitiesLabel}>
                              {`GP${index + 1}:`}
                            </span>
                            <span className={typeaheadStyles.obstaclesAndOpportunitiesReadOnlyText}>
                              {parseAndExtractTextFromLexicalJson(entry.strategy_value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Text size="small" color="subtle">
                        -
                      </Text>
                    )}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          ) : (
            <div className={styles.updateSectionCard}>
              <div className={tableStyles.redesignZeroStateTriggerRow}>
                <CreateSwitchboardZeroStateTrigger
                  workstreamId={workstream.id}
                  analyticsSource="strategy_section"
                />
              </div>
            </div>
          )}
        </FadeIn>
      ) : null}
    </section>
  );
};
